import React, { useState } from "react";
import ParticlesBackground from "../components/Effects/ParticlesBackground";
import "../styles/pages/History.css";
import { useLanguage } from "../contexts/LanguageContext";

interface HistorySection {
  id: string;
  title: string;
  content: string;
  era?: string;
  factions?: Faction[];
}

interface Faction {
  id: string;
  title: string;
  content: string;
}

const History: React.FC = () => {
  const { t } = useLanguage();
  const [openSection, setOpenSection] = useState<string | null>("era-magica");
  const [openFaction, setOpenFaction] = useState<string | null>(null);

  const toggleSection = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };

  const toggleFaction = (factionId: string) => {
    setOpenFaction(openFaction === factionId ? null : factionId);
  };
  const factions: Faction[] = [
    {
      id: "sages",
      title: t("history", "sections.sages.title"),
      content: t("history", "sections.sages.content"),
    },
    {
      id: "marked",
      title: t("history", "sections.marked.title"),
      content: t("history", "sections.marked.content"),
    },
    {
      id: "guardians",
      title: t("history", "sections.guardians.title"),
      content: t("history", "sections.guardians.content"),
    },
    {
      id: "market",
      title: t("history", "sections.market.title"),
      content: t("history", "sections.market.content"),
    },
    {
      id: "weavers",
      title: t("history", "sections.weavers.title"),
      content: t("history", "sections.weavers.content"),
    },
    {
      id: "occultists",
      title: t("history", "sections.occultists.title"),
      content: t("history", "sections.occultists.content"),
    },
    {
      id: "reclaimers",
      title: t("history", "sections.reclaimers.title"),
      content: t("history", "sections.reclaimers.content"),
    },
    {
      id: "allies",
      title: t("history", "sections.allies.title"),
      content: t("history", "sections.allies.content"),
    },
  ];

  const historySections: HistorySection[] = [
    {
      id: "magic-era",
      title: t("history", "sections.magic-era.title"),
      era: t("history", "sections.magic-era.era"),
      content: t("history", "sections.magic-era.content"),
    },
    {
      id: "magic-abuse",
      title: t("history", "sections.magic-abuse.title"),
      era: t("history", "sections.magic-abuse.era"),
      content: t("history", "sections.magic-abuse.content"),
    },
    {
      id: "collapse",
      title: t("history", "sections.collapse.title"),
      era: t("history", "sections.collapse.era"),
      content: t("history", "sections.collapse.content"),
    },
    {
      id: "technological-era",
      title: t("history", "sections.technological-era.title"),
      era: t("history", "sections.technological-era.era"),
      content: t("history", "sections.technological-era.content"),
    },
    {
      id: "reality-crack",
      title: t("history", "sections.reality-crack.title"),
      era: t("history", "sections.reality-crack.era"),
      content: t("history", "sections.reality-crack.content"),
    },
    {
      id: "residual-chaos",
      title: t("history", "sections.residual-chaos.title"),
      era: t("history", "sections.residual-chaos.era"),
      content: t("history", "sections.residual-chaos.content"),
    },
    {
      id: "new-world",
      title: t("history", "sections.new-world.title"),
      era: t("history", "sections.new-world.era"),
      content: t("history", "sections.new-world.content"),
    },
    {
      id: "factions",
      title: t("history", "sections.factions.title"),
      era: t("history", "sections.factions.era"),
      content: t("history", "sections.factions.content"),
      factions: factions,
    },
  ];

  return (
    <div className="history-page">
      <ParticlesBackground />

      <div className="history-container">
        <div className="history-header">
          <h1 className="history-title">{t("history", "title")}</h1>
          <p className="history-subtitle">{t("history", "subtitle")}</p>
          <div className="timeline-bar">
            <div className="timeline-progress"></div>
          </div>
        </div>

        <div className="history-content">
          {historySections.map((section, index) => (
            <div
              key={section.id}
              className={`history-section ${
                openSection === section.id ? "active" : ""
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div
                className="section-header"
                onClick={() => toggleSection(section.id)}
              >
                <div className="section-title-content">
                  <span className="era-badge">{section.era}</span>
                  <h3 className="section-title">{section.title}</h3>
                </div>
                <div className="section-indicator">
                  {openSection === section.id ? "−" : "+"}
                </div>
              </div>

              <div className="section-content">
                <div className="content-wrapper">
                  <p>{section.content}</p>

                  {section.factions && (
                    <div className="factions-list">
                      {section.factions.map((faction) => (
                        <div
                          key={faction.id}
                          className={`faction-item ${
                            openFaction === faction.id ? "active" : ""
                          }`}
                        >
                          <div
                            className="faction-header"
                            onClick={() => toggleFaction(faction.id)}
                          >
                            <h4 className="faction-title">{faction.title}</h4>
                            <div className="faction-indicator">
                              {openFaction === faction.id ? "−" : "+"}
                            </div>
                          </div>
                          <div className="faction-content">
                            <p>{faction.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {section.id === "new-world" && (
                    <div className="story-note">
                      <p>
                        ✨{" "}
                        <strong>
                          {t("history", "sections.your-history.title")}
                        </strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="history-footer">
          <p>{t("history", "sections.the-legend.title")}</p>
        </div>
      </div>
    </div>
  );
};

export default History;
